/**
 * Importação de filtro composto a partir de CSV (modal #filter).
 * Colunas: campo, operador, dado, conector (opcionais: abre, fecha para parênteses 0/1).
 */
import { notify } from './msgbox.js';

/** @param {string} s */
function norm(s)
{
	return String(s ?? '')
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
}

/** Chaves sem acento e sem espaços (ver `norm` + compact) */
const OP_ALIASES = new Map([
	['igual', 'eq'], ['eq', 'eq'],
	['diferente', 'ne'], ['ne', 'ne'],
	['maior', 'gt'], ['gt', 'gt'],
	['menor', 'lt'], ['lt', 'lt'],
	['maiorouigual', 'ge'], ['ge', 'ge'],
	['menorouigual', 'le'], ['le', 'le'],
	['similar', 'si'], ['si', 'si'],
	['contem', 'co'], ['co', 'co'],
	['naocontem', 'nc'], ['nc', 'nc'],
	['comececom', 'bw'], ['bw', 'bw'],
	['naocomececom', 'nb'], ['nb', 'nb'],
	['terminecom', 'ew'], ['ew', 'ew'],
	['naoterminecom', 'nd'], ['nd', 'nd'],
	['enulo', 'nu'], ['nulo', 'nu'], ['nu', 'nu'],
	['naoenulo', 'nn'], ['nn', 'nn'],
]);

const FIELD_ALIASES = {
	saida: 'id',
	numerosaida: 'id',
	pedido: 'order_id',
	pedidosaida: 'order_id',
};

/**
 * Quebra uma linha CSV com separador `;` ou `,`, respeitando aspas duplas.
 * @param {string} line
 * @param {string} sep
 * @returns {string[]}
 */
export function splitCsvLine(line, sep)
{
	const out = [];
	let cur = '';
	let inQ = false;
	for (let i = 0; i < line.length; i++)
	{
		const c = line[i];
		if (c === '"')
		{
			inQ = !inQ;
			continue;
		}
		if (!inQ && c === sep)
		{
			out.push(cur.trim());
			cur = '';
			continue;
		}
		cur += c;
	}
	out.push(cur.trim());
	return out;
}

/**
 * @param {string} cell
 * @returns {string}
 */
function detectSeparator(firstLine)
{
	const sc = (firstLine.match(/;/g) || []).length;
	const cc = (firstLine.match(/,/g) || []).length;
	return sc >= cc ? ';' : ',';
}

/**
 * @param {string} token
 * @param {{ field: string, label?: string }[]} fieldDefs
 * @returns {string}
 */
function resolveField(token, fieldDefs)
{
	const t = norm(token);
	if (!t)
		throw new Error('Campo vazio numa linha do CSV.');
	const byField = fieldDefs.find(f => norm(f.field) === t);
	if (byField)
		return byField.field;
	const byLabel = fieldDefs.find(f => f.label && norm(f.label) === t);
	if (byLabel)
		return byLabel.field;
	const alias = FIELD_ALIASES[t];
	if (alias && fieldDefs.some(f => f.field === alias))
		return alias;
	throw new Error(`Campo desconhecido: "${String(token).trim()}". Use o nome técnico ou o rótulo da lista (ex.: id, Saída).`);
}

/**
 * @param {string} token
 * @returns {string}
 */
function resolveOperator(token)
{
	const t = norm(token);
	if (!t)
		throw new Error('Operador vazio.');
	const compact = t.replace(/\s+/g, '');
	if (OP_ALIASES.has(compact))
		return OP_ALIASES.get(compact);
	/* Símbolos soltos */
	const sym = String(token).trim();
	if (sym === '=') return 'eq';
	if (sym === '!=') return 'ne';
	if (sym === '>') return 'gt';
	if (sym === '<') return 'lt';
	if (sym === '>=') return 'ge';
	if (sym === '<=') return 'le';
	if (/^[a-z]{2}$/.test(compact))
		return compact;
	throw new Error(`Operador desconhecido: "${String(token).trim()}". Ex.: igual, contém, eq, co.`);
}

/**
 * @param {string} token
 * @param {boolean} isLastRow
 * @returns {string|number}
 */
function resolveConnector(token, isLastRow)
{
	const raw = String(token ?? '').trim();
	if (isLastRow)
	{
		if (!raw || raw === '-1')
			return '';
	}
	if (!raw && !isLastRow)
		return ',';
	const n = norm(raw);
	if (n === 'e' || n === 'and' || n === 'y' || raw === ',')
		return ',';
	if (n === 'ou' || n === 'or' || raw === ';')
		return ';';
	throw new Error(`Conector inválido: "${raw}". Use E/AND (,) ou Ou/OR (;).`);
}

/**
 * @param {string} raw
 * @returns {number}
 */
function parse01(raw)
{
	const s = String(raw ?? '').trim();
	if (s === '' || s === '0')
		return 0;
	if (s === '1')
		return 1;
	throw new Error(`abre/fecha: use 0 ou 1 (recebido: "${raw}").`);
}

/**
 * Converte texto CSV em objeto de valores do formulário #filter (field-0, operator-0, …).
 * @param {string} text
 * @param {{ field: string, label?: string }[]} fieldDefs
 * @returns {Record<string, string|number>}
 */
export function parseFilterCsv(text, fieldDefs)
{
	if (!fieldDefs || !fieldDefs.length)
		throw new Error('Lista de campos do filtro indisponível.');
	const lines = String(text).replace(/^\uFEFF/, '').split(/\r?\n/).map(l => l.trim()).filter(l => l.length && !l.startsWith('#'));
	if (lines.length === 0)
		throw new Error('Arquivo vazio ou só com comentários.');
	const sep = detectSeparator(lines[0]);
	let headerIdx = { campo: 0, operador: 1, dado: 2, conector: 3, abre: -1, fecha: -1 };
	const firstCells = splitCsvLine(lines[0], sep).map(c => norm(c.replace(/^"|"$/g, '')));
	const looksHeader = firstCells.some(c => ['campo', 'field', 'operador', 'operator', 'dado', 'value', 'valor', 'conector', 'connector'].includes(c));
	let dataLines = lines;
	if (looksHeader)
	{
		const h = firstCells;
		const find = (names) =>
		{
			for (let i = 0; i < h.length; i++)
				if (names.includes(h[i]))
					return i;
			return -1;
		};
		headerIdx.campo = find(['campo', 'field', 'coluna']);
		headerIdx.operador = find(['operador', 'operator', 'op']);
		headerIdx.dado = find(['dado', 'value', 'valor']);
		headerIdx.conector = find(['conector', 'connector', 'conn']);
		headerIdx.abre = find(['abre', 'open']);
		headerIdx.fecha = find(['fecha', 'close']);
		if (headerIdx.campo < 0 || headerIdx.operador < 0 || headerIdx.dado < 0)
			throw new Error('Cabeçalho CSV: informe colunas campo, operador e dado.');
		dataLines = lines.slice(1);
	}
	const out = {};
	const rows = [];
	for (let li = 0; li < dataLines.length; li++)
	{
		const cells = splitCsvLine(dataLines[li], sep);
		if (cells.length < 3)
			continue;
		const fi = headerIdx.campo >= 0 ? cells[headerIdx.campo] : cells[0];
		const oi = headerIdx.operador >= 0 ? cells[headerIdx.operador] : cells[1];
		const vi = headerIdx.dado >= 0 ? cells[headerIdx.dado] : cells[2];
		const ci = headerIdx.conector >= 0 ? cells[headerIdx.conector] : cells[3];
		const oa = headerIdx.abre >= 0 ? cells[headerIdx.abre] : (cells[4] ?? '');
		const cl = headerIdx.fecha >= 0 ? cells[headerIdx.fecha] : (cells[5] ?? '');
		if (!String(fi ?? '').trim() && !String(oi ?? '').trim() && !String(vi ?? '').trim())
			continue;
		rows.push({ fi, oi, vi, ci, oa, cl });
	}
	if (rows.length === 0)
		throw new Error('Nenhuma linha de filtro válida.');
	for (let i = 0; i < rows.length; i++)
	{
		const { fi, oi, vi, ci, oa, cl } = rows[i];
		const isLast = i === rows.length - 1;
		out[`field-${i}`] = resolveField(fi, fieldDefs);
		out[`operator-${i}`] = resolveOperator(oi);
		out[`value-${i}`] = String(vi ?? '');
		const nu = out[`operator-${i}`] === 'nu' || out[`operator-${i}`] === 'nn';
		if (!nu && !String(vi ?? '').trim())
			throw new Error(`Linha ${i + 1}: informe o dado (ou use operador "É nulo" / "Não é nulo").`);
		out[`connector-${i}`] = resolveConnector(ci, isLast);
		out[`open-${i}`] = oa === undefined || oa === '' ? 0 : parse01(oa);
		out[`close-${i}`] = cl === undefined || cl === '' ? 0 : parse01(cl);
	}
	return out;
}

/**
 * Gera conteúdo do arquivo modelo (UTF-8 com BOM no download).
 * @param {{ field: string, label?: string }[]} fieldDefs
 */
export function buildFilterCsvTemplate(fieldDefs)
{
	const list = (fieldDefs || [])
		.map(f => `${f.field}${f.label ? ` (${f.label})` : ''}`)
		.join('; ');
	const head = 'campo;operador;dado;conector;abre;fecha\n';
	const hint = '# campo: nome API ou rótulo (ex.: id ou Saída). operador: igual, contém, eq, co… conector: e ou ou. Última linha: conector vazio. abre/fecha: 0 ou 1.\n';
	const example = `id;igual;12345;e;0;0\nid;igual;12346;;0;0\n`;
	const extra = list ? `# Campos desta tela: ${list}\n` : '';
	return hint + extra + head + example;
}

/** Anexa botões de importação/modelo ao modal #filter (após o formset existir). */
export function setupFilterCsvImport(filterInstance)
{
	const formNode = document.querySelector('#filter');
	if (!formNode)
		return;
	const fileInput = formNode.querySelector('#filter-import-csv');
	const btnImport = formNode.querySelector('#filter-btn-import-csv');
	const btnTemplate = formNode.querySelector('#filter-btn-csv-template');
	const fieldDefs = filterInstance.fields || [];

	btnTemplate?.addEventListener('click', (e) =>
	{
		e.preventDefault();
		const body = buildFilterCsvTemplate(fieldDefs);
		const blob = new Blob(['\ufeff' + body], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'modelo-filtro-sial.csv';
		a.click();
		URL.revokeObjectURL(url);
	});

	btnImport?.addEventListener('click', () => fileInput?.click());

	fileInput?.addEventListener('change', () =>
	{
		const f = fileInput.files?.[0];
		if (!f)
			return;
		const reader = new FileReader();
		reader.onload = () =>
		{
			try
			{
				const text = String(reader.result || '').replace(/^\uFEFF/, '');
				const data = parseFilterCsv(text, fieldDefs);
				const n = Object.keys(data).filter(k => /^field-\d+$/.test(k)).length;
				const fs = filterInstance.formset;
				if (!fs)
					throw new Error('Formulário de filtro não está pronto.');
				const have = fs.getTableSize();
				if (n > have)
					fs.appendRows(n - have, '.row');
				else if (n < have)
				{
					for (let r = have - 1; r >= n; r--)
						fs.removeRows([r], '.row');
				}
				fs.form.eraseValues();
				fs.form.setValues(data);
				fs.form.refresh();
				notify(`Filtro importado: ${n} linha(s). Revise e clique em Aplicar.`, 'Filtro');
			}
			catch (err)
			{
				notify(err?.message || String(err), 'Importar CSV');
			}
			fileInput.value = '';
		};
		reader.onerror = () =>
		{
			notify('Não foi possível ler o arquivo.', 'Importar CSV');
			fileInput.value = '';
		};
		reader.readAsText(f, 'UTF-8');
	});
}
